// MERGE branch structural-edit planners (§2.5 S7): add-merge-branch /
// remove-merge-branch / modify-merge-branch-action. Sits beside
// applyStructuralEdit.merge.ts (branch-CONTENT edits: condition + nested SET)
// and reuses its re-scan idiom + `pickBranch` addressing.
//
// The load-bearing piece is `mergeBranchContentEnd`: `MergeBranch.range` starts
// AFTER the WHEN keyword and the LAST branch's range swallows the statement
// tail (trailing `;`, MSSQL `OUTPUT …`, PG17 `RETURNING …`), so every planner
// here bounds itself by the branch's true content end instead of trusting
// `range.endOffset`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — MERGE branch ops (§2.5 S7).

import type { MergeBranch, PlannedByteEdit, StructuralEdit } from '../types';
import type { MergeBranchKind } from '../types/structuralEdit';
import { scanTopLevelKeywords, type TopKeyword } from '../structure/scanner';

import type { EditCtx } from './applyStructuralEdit';
import { pickBranch } from './applyStructuralEdit.merge';
import {
  deleteEdit,
  insertEdit,
  replaceEdit,
  trimTrailingWs,
} from './applyStructuralEdit.shared';

type MergeBranchesEdit = Extract<
  StructuralEdit,
  { op: 'add-merge-branch' | 'remove-merge-branch' | 'modify-merge-branch-action' }
>;

export function planMergeBranchesEdit(
  ctx: EditCtx,
  edit: MergeBranchesEdit,
): readonly PlannedByteEdit[] {
  switch (edit.op) {
    case 'add-merge-branch':
      return planAddBranch(ctx, edit.branchText);
    case 'remove-merge-branch':
      return planRemoveBranch(ctx, edit.branchKind, edit.branchIndex);
    case 'modify-merge-branch-action':
      return planActionSwitch(ctx, edit);
  }
}

// mergeBranchContentEnd ------------------------------------------------------
// The branch's true content end: min(next top-level WHEN start, first top-level
// OUTPUT / RETURNING keyword after the branch start, a statement-trailing `;`,
// `range.endOffset` / statement end), whitespace-trimmed. The document splitter
// normally emits statement ranges that EXCLUDE the trailing delimiter, but
// wider ranges (whole-buffer fallbacks) include it — both shapes are handled.
// Exported for unit tests and future planners.
export function mergeBranchContentEnd(ctx: EditCtx, branch: MergeBranch): number {
  const { source, options, statementInfo } = ctx;
  const start = branch.range.startOffset;
  const stmtEnd = statementInfo.range.endOffset;
  let bound = Math.min(branch.range.endOffset, stmtEnd);
  const kws = scanTopLevelKeywords(source, start, stmtEnd, options);
  for (const k of kws) {
    if (k.upper === 'WHEN' || k.upper === 'OUTPUT' || k.upper === 'RETURNING') {
      if (k.start < bound) bound = k.start;
      break;
    }
  }
  const semi = trailingSemicolonOffset(ctx);
  if (semi !== null && semi >= start && semi < bound) bound = semi;
  return Math.max(trimTrailingWs(source, start, bound), start);
}

// Offset of the statement-trailing `;` when the statement range includes one
// (after trailing whitespace), else null.
function trailingSemicolonOffset(ctx: EditCtx): number | null {
  const { source, statementInfo } = ctx;
  const stmtStart = statementInfo.range.startOffset;
  const e = trimTrailingWs(source, stmtStart, statementInfo.range.endOffset);
  return e > stmtStart && source[e - 1] === ';' ? e - 1 : null;
}

// remove-merge-branch --------------------------------------------------------
function planRemoveBranch(
  ctx: EditCtx,
  kind: MergeBranchKind,
  index: number,
): readonly PlannedByteEdit[] {
  const branch = pickBranch(ctx, kind, index);
  if (!branch) return [];
  // Decision 6 (defense in depth — the wrapper + UI also refuse): a WHEN-less
  // MERGE is invalid on every dialect that has MERGE, so removing the last
  // remaining branch plans nothing.
  if (allBranches(ctx).length <= 1) return [];
  const whenKw = findOwningWhen(ctx, branch);
  if (!whenKw) return [];
  // Whitespace normalization: extend the deletion left over the run before the
  // WHEN keyword, so exactly one separator (the one before the FOLLOWING
  // element) survives — no double spaces mid-statement, no stranded blank line.
  const start = trimTrailingWs(ctx.source, ctx.statementInfo.range.startOffset, whenKw.start);
  return [deleteEdit(ctx, start, mergeBranchContentEnd(ctx, branch), 'remove-merge-branch')];
}

// `MergeBranch.range` excludes its WHEN keyword — re-locate the owner by scan:
// the top-level WHEN with maximal start whose end <= range.startOffset.
function findOwningWhen(ctx: EditCtx, branch: MergeBranch): TopKeyword | null {
  const { source, options, statementInfo } = ctx;
  const kws = scanTopLevelKeywords(
    source, statementInfo.range.startOffset, statementInfo.range.endOffset, options,
  );
  let owner: TopKeyword | null = null;
  for (const k of kws) {
    if (k.upper !== 'WHEN' || k.end > branch.range.startOffset) continue;
    if (!owner || k.start > owner.start) owner = k;
  }
  return owner;
}

// add-merge-branch -----------------------------------------------------------
function planAddBranch(ctx: EditCtx, branchText: string): readonly PlannedByteEdit[] {
  const text = branchText.trim();
  if (text.length === 0) return [];
  const insertAt = branchInsertOffset(ctx);
  if (insertAt === null) return [];
  const sep = statementSpansMultipleLines(ctx) ? '\n' : ' ';
  return [insertEdit(ctx, insertAt, `${sep}${text}`, 'add-merge-branch')];
}

function branchInsertOffset(ctx: EditCtx): number | null {
  const branches = allBranches(ctx);
  if (branches.length > 0) {
    // DOCUMENT order, not bucket order: the buckets (matched / not-matched)
    // lose the source interleave, so the last branch is the max range start
    // across BOTH lists.
    let last = branches[0];
    for (const b of branches) {
      if (b.range.startOffset > last.range.startOffset) last = b;
    }
    return mergeBranchContentEnd(ctx, last);
  }
  // Zero-WHEN statement: insert after the ON predicate (falling back to the
  // USING source / target), under the same tail bounds.
  if (!ctx.structure.mergeTarget) return null;
  return zeroBranchInsertOffset(ctx);
}

function allBranches(ctx: EditCtx): readonly MergeBranch[] {
  return [
    ...(ctx.structure.mergeWhenMatched ?? []),
    ...(ctx.structure.mergeWhenNotMatched ?? []),
  ];
}

function zeroBranchInsertOffset(ctx: EditCtx): number {
  const { source, options, statementInfo, structure } = ctx;
  const on = structure.mergeOn;
  const anchor = on && on.length > 0
    ? on[on.length - 1].range.endOffset
    : (structure.mergeSource ?? structure.mergeTarget)!.range.endOffset;
  const stmtEnd = statementInfo.range.endOffset;
  let bound = stmtEnd;
  const kws = scanTopLevelKeywords(source, anchor, stmtEnd, options);
  for (const k of kws) {
    if (k.upper === 'OUTPUT' || k.upper === 'RETURNING') {
      if (k.start < bound) bound = k.start;
      break;
    }
  }
  const semi = trailingSemicolonOffset(ctx);
  if (semi !== null && semi < bound) bound = semi;
  // Defensive min with the anchor: ON-fragment ranges are trimmed today, but a
  // range that swallowed tail bytes would otherwise push the insert past `;`.
  return trimTrailingWs(source, statementInfo.range.startOffset, Math.min(anchor, bound));
}

// Decision 5: `\n` separator when the statement already spans multiple lines,
// else a single space.
function statementSpansMultipleLines(ctx: EditCtx): boolean {
  const { source, statementInfo } = ctx;
  const start = statementInfo.range.startOffset;
  const end = trimTrailingWs(source, start, statementInfo.range.endOffset);
  return source.slice(start, end).includes('\n');
}

// modify-merge-branch-action -------------------------------------------------
// Replaces `[thenKw.end, mergeBranchContentEnd)` with ` ${newActionText}` —
// the AND condition before THEN stays byte-identical. THEN is re-located
// inside the branch span (the findConditionByteRange idiom in merge.ts).
function planActionSwitch(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-merge-branch-action' }>,
): readonly PlannedByteEdit[] {
  const v = edit.newActionText.trim();
  if (v.length === 0) return [];
  const branch = pickBranch(ctx, edit.branchKind, edit.branchIndex);
  if (!branch) return [];
  const kws = scanTopLevelKeywords(
    ctx.source, branch.range.startOffset, branch.range.endOffset, ctx.options,
  );
  const thenKw = kws.find((k) => k.upper === 'THEN');
  if (!thenKw) return [];
  const end = mergeBranchContentEnd(ctx, branch);
  if (end <= thenKw.end) return [];
  return [replaceEdit(ctx, thenKw.end, end, ` ${v}`, 'modify-merge-branch-action')];
}
